import { useEffect, useMemo, useState } from "react";
import type { FaqItem } from "@/components/feature/FaqAccordion";
import {
  fetchPublicSeo,
  fetchPublicTestimonials,
  getPublicFaqs,
  getPublicSeo,
  loadPublicFaqs,
  subscribePublicFaqs,
  subscribePublicSeo,
  type PublicFaqRow,
  type PublicSeo,
  type PublicTestimonial,
} from "@/lib/publicContent";

/**
 * FAQ items for a given scope, read from the backend. Falls back to the curated
 * list while loading or if the backend has no questions for that scope yet.
 */
export function usePublicFaqs(scope: string, fallback: FaqItem[]): FaqItem[] {
  const [rows, setRows] = useState<PublicFaqRow[]>(getPublicFaqs());

  useEffect(() => {
    let active = true;
    const unsubscribe = subscribePublicFaqs((next) => {
      if (active) setRows(next);
    });
    loadPublicFaqs().then((next) => {
      if (active) setRows(next);
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  const scoped = useMemo(
    () =>
      rows
        .filter((row) => row.scope === scope)
        .map((row) => ({ q: row.question, a: row.answer })),
    [rows, scope],
  );

  return scoped.length > 0 ? scoped : fallback;
}

export interface TestimonialsResource {
  data: PublicTestimonial[];
  loading: boolean;
  error: string;
  reload: () => void;
}

/** Published homepage testimonials, read from the backend. */
export function usePublicTestimonials(): TestimonialsResource {
  const [data, setData] = useState<PublicTestimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const reload = () => {
    setLoading(true);
    setError("");
    fetchPublicTestimonials()
      .then((result) => setData(result))
      .catch(() => setError("We couldn't load testimonials right now."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, []);

  return { data, loading, error, reload };
}

/** Page-level SEO overrides from the backend for the given path. */
export function usePublicSeo(path: string): PublicSeo | null {
  const [override, setOverride] = useState<PublicSeo | null>(() => getPublicSeo(path));

  useEffect(() => {
    if (!path) {
      setOverride(null);
      return;
    }
    let active = true;
    setOverride(getPublicSeo(path));
    const unsubscribe = subscribePublicSeo(path, (next) => {
      if (active) setOverride(next);
    });
    fetchPublicSeo(path).then((result) => {
      if (active) setOverride(result);
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [path]);

  return override;
}